<template>
  <div class="audit-reconcile-cell">

    <!-- Read-only: manager access required -->
    <el-tooltip v-if="readOnly" content="Manager access required to reconcile." placement="top">
      <span class="audit-reconcile-cell__readonly">—</span>
    </el-tooltip>

    <!-- Editable: popover with per-issue reconcile blocks -->
    <el-popover
      v-else
      ref="popover"
      trigger="click"
      placement="left-start"
      :width="320">

      <div class="audit-reconcile-cell__popover">

        <!-- Phase 0: no_rate — assign rate block -->
        <div v-if="issueTypes.indexOf('no_rate') >= 0" class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Assign rate</p>

          <!-- Suggested rate shortcut -->
          <div v-if="row.suggestedRate" class="audit-reconcile-cell__suggested">
            <el-button
              size="mini"
              type="success"
              icon="fa fa-check"
              @click="acceptSuggested">
              Accept suggested: {{ row.suggestedRate.name }}
            </el-button>
          </div>

          <select-sharer-rates
            ref="rateSelect"
            :sharer-id="row.organisation && row.organisation.id"
            v-model="rateId"
            placeholder="Choose a rate…"
            label=""
            :no-form="true" />

          <div class="audit-reconcile-cell__footer">
            <el-button
              type="primary"
              size="mini"
              :disabled="!rateId"
              @click="assign">
              Assign rate
            </el-button>
          </div>
        </div>

        <!-- XOL-467 rectify redesign (2026-09-02, supersedes the XOL-483/484
             branching blocks above): ONE form, all THREE dates always
             rendered, submitted together via `saveDates`. The block still
             gates on having a date issue at all — only the per-issue
             branching (separate Handover vs. separate "Set Agreement Start"
             blocks) is gone. Each field is pre-filled from the row's stored
             value where it has one (data()) and left empty where it
             doesn't. `dateIssueDetail` still defers to the API's per-row
             `issue.detail` rather than a locally-invented sentence. -->
        <div v-if="showDateBlock" class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Set dates</p>
          <p class="audit-reconcile-cell__hint">{{ dateIssueDetail }}</p>

          <div class="audit-reconcile-cell__field">
            <p class="audit-reconcile-cell__field-label">Handover</p>
            <date-input
              :no-form="true"
              v-model="dateHandover"
              label=""
              placeholder="Pick handover date…"
              @change="clearDateFieldErrors" />
            <!-- `:no-form` makes DateInput's `error` prop inert (it renders a
                 plain div, not an el-form-item) — render the failure as text. -->
            <p v-if="dateFieldErrors.handover" class="audit-reconcile-cell__error">{{ dateFieldErrors.handover }}</p>
          </div>

          <div class="audit-reconcile-cell__field">
            <p class="audit-reconcile-cell__field-label">Agreement Start</p>
            <date-input
              :no-form="true"
              v-model="dateStarted"
              label=""
              placeholder="Pick agreement start date…"
              @change="clearDateFieldErrors" />
            <p v-if="dateFieldErrors.started" class="audit-reconcile-cell__error">{{ dateFieldErrors.started }}</p>
          </div>

          <div class="audit-reconcile-cell__field">
            <!-- XOL-467 (2026-09-02, Carlos): Billing Start is legitimately
                 optional — the customer may not be ready to be billed yet.
                 Leaving it blank is not a failure state; the row keeps
                 reporting `no_billing_start` in the audit, which is the
                 intended, informative outcome. Never required, never
                 auto-filled from this field, never warned about here. -->
            <p class="audit-reconcile-cell__field-label">Billing Start (optional)</p>
            <date-input
              :no-form="true"
              v-model="dateStartBilling"
              label=""
              placeholder="Pick billing start date…"
              @change="clearDateFieldErrors" />
            <p v-if="dateFieldErrors.startBilling" class="audit-reconcile-cell__error">{{ dateFieldErrors.startBilling }}</p>
          </div>

          <!-- Orchestrator addendum (2026-09-02), pinned API-side as
               `test_nulling_both_dates_on_a_handover_row_rewrites_them_from_the_handover`:
               on a row with a Handover, submitting Agreement Start AND
               Billing Start both blank does not clear them — the API's
               cascade re-derives both from the Handover. Not blocked (it is
               a legitimate one-click reset), just surfaced so it isn't a
               surprise. Deliberately NOT shown for the Billing-Start-only
               case, which is the intended unwarned path. -->
          <p v-if="willRederiveFromHandover" class="audit-reconcile-cell__warning">
            Agreement Start and Billing Start are both empty — saving will set both to the Handover date ({{ dateHandover }}) rather than leaving them blank.
          </p>

          <div class="audit-reconcile-cell__footer">
            <el-button
              type="primary"
              size="mini"
              @click="saveDates">
              Set dates
            </el-button>
          </div>
        </div>

        <!-- Phase 1: expired_agreement / expiring_soon — extend or terminate -->
        <!-- Rendered once if either type is present (de-duped) -->
        <div
          v-if="issueTypes.indexOf('expired_agreement') >= 0 || issueTypes.indexOf('expiring_soon') >= 0 || issueTypes.indexOf('expiry_before_billing_start') >= 0"
          class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Extend agreement</p>
          <date-input
            :no-form="true"
            v-model="expiryDate"
            label=""
            placeholder="Pick new expiry date…" />
          <!-- `:no-form` makes DateInput's `error` prop inert — render the failure as text. -->
          <p v-if="expiryError" class="audit-reconcile-cell__error">{{ expiryError }}</p>
          <div class="audit-reconcile-cell__footer audit-reconcile-cell__footer--spread">
            <el-button
              type="primary"
              size="mini"
              :disabled="!expiryDate"
              @click="extendExpiry">
              Extend
            </el-button>
            <el-button
              type="danger"
              size="mini"
              @click="softTerminate">
              Terminate (soft-end)
            </el-button>
          </div>
        </div>

        <!-- Phase 2: empty_override — a DECISION point with two legitimate
             exits, not a fault with one correct repair (XOL-481, see the
             Decisions section of the XOL-481 plan doc for the full
             correction). The detector is `rateLocked = 1 AND currentRate IN
             (NULL, 0)` — it carries NO `rate_id` clause, so whether the
             licensee sits on a rate card is irrelevant to it. The customer
             either enters the fee, or takes the sharer off the lock; neither
             is demoted, neither is discouraged.
             XOL-471 retired `override_without_rate`. It was never a real check:
             a licensee negotiated at a flat fee is not attached to a rate card
             at all, so there was never anything for an override to fall back
             to. That was equally true before XOL-368 locked every rate — the
             shape was merely rare then, and rarity got mistaken for fault.
             Frequency is not evidence. `empty_override` is the check that was
             actually wanted, and it keeps this block alive. -->
        <div
          v-if="issueTypes.indexOf('empty_override') >= 0"
          class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Rate override</p>
          <p class="audit-reconcile-cell__hint">This sharer is on a rate override but no fee was entered, so nothing is billed. Enter the fee, or remove the override.</p>

          <!-- XOL-481: always rendered so the user sees the deciding fact
               before choosing. Deliberately NOT `row.currentRate` — this
               block only renders when `currentRate` is 0/null (that IS the
               issue), so a "current fee" readout would print RM0.00 on every
               row it renders on and repeat what the chip already says. -->
          <div class="audit-reconcile-cell__readout">
            <p class="audit-reconcile-cell__readout-label">Calculated (auto) rate</p>
            <span class="audit-reconcile-cell__readout-value">{{ calcRateDisplay }}</span>
          </div>
          <!-- Neutral statement of fact, not a warning — tells the user what
               removing the override would leave behind so they can choose.
               Deliberately NOT "no rate card": calculatedRate is also null
               when billingPosition() === 0 (an unassigned Sharer #). -->
          <p v-if="!hasCalculatedFallback" class="audit-reconcile-cell__hint">No rate behind the override.</p>

          <!-- Renders on 0 of 302 rows today; the rule is still right and the
               value, when non-zero, is the best available hint at the fee. -->
          <div v-if="row.currentLegacyBill > 0" class="audit-reconcile-cell__readout">
            <p class="audit-reconcile-cell__readout-label">Legacy bill</p>
            <span class="audit-reconcile-cell__readout-value">{{ formatMoney(row.currentLegacyBill, 2) }}</span>
          </div>

          <div class="audit-reconcile-cell__field">
            <p class="audit-reconcile-cell__field-label">Fee</p>
            <currency-input
              :no-form="true"
              v-model="overrideAmount"
              label=""
              placeholder="Amount agreed with this sharer"
              @change="clearOverrideError" />
            <!-- `:no-form` makes CurrencyInput's `error` prop inert — render
                 the failure as text. `role="alert"` is required, not
                 decorative: it is the accessible answer to that inertness,
                 since plain text injected by v-if is otherwise announced to
                 nobody. -->
            <p v-if="overrideError" class="audit-reconcile-cell__error" role="alert">{{ overrideError }}</p>
          </div>

          <div class="audit-reconcile-cell__footer audit-reconcile-cell__footer--spread">
            <!-- XOL-481 load-bearing constraint — do NOT bind :disabled to
                 overrideAmount here. CurrencyInput only commits its value on
                 @change (blur), so a button disabled until the amount is
                 non-zero would swallow the user's FIRST click: the click
                 blurs the field (committing the value) but lands on a still-
                 disabled button. Validate in the handler instead, which runs
                 after the blur has committed. Same trap documented at
                 BulkRateAssign.vue:367-372 — do not "fix" this back. -->
            <el-button
              type="primary"
              size="mini"
              @click="saveOverride">
              Set fee
            </el-button>
            <!-- Solid, not plain — co-equal exit, not demoted. Enter the fee
                 or take the sharer off the lock are BOTH legitimate answers;
                 the customer decides. `warning` not `danger` — clearing
                 destroys no data. -->
            <el-button
              type="warning"
              size="mini"
              @click="clearOverride">
              Clear override
            </el-button>
          </div>
        </div>

        <!-- Phase 2: terminated_no_end — set termination end date -->
        <div v-if="issueTypes.indexOf('terminated_no_end') >= 0" class="audit-reconcile-cell__block">
          <p class="audit-reconcile-cell__label">Set termination end date</p>
          <date-input
            :no-form="true"
            v-model="endedDate"
            label=""
            placeholder="Pick end date…" />
          <div class="audit-reconcile-cell__footer">
            <el-button
              type="primary"
              size="mini"
              :disabled="!endedDate"
              @click="setEndDate">
              Set end date
            </el-button>
          </div>
        </div>

        <!-- Phase 2 + XOL-376 Inc 4 + XOL-416 + XOL-487: advisory block —
             orphan_version_pin, no_structural_match, missing_structure_height,
             ambiguous_billing_mode, duplicate_sharer_no, zero_sharer_no,
             position_below_min_sharer, strict_position_out_of_range,
             strict_term_exceeded (no one-click fix) -->
        <div v-if="advisoryIssues.length > 0" class="audit-reconcile-cell__block audit-reconcile-cell__block--advisory">
          <p class="audit-reconcile-cell__label audit-reconcile-cell__label--muted">Needs manual review</p>
          <ul class="audit-reconcile-cell__advisory-list">
            <li
              v-for="issue in advisoryIssues"
              :key="issue.type"
              class="audit-reconcile-cell__advisory-item">
              {{ issue.detail || issue.type }}
            </li>
          </ul>
        </div>

        <!-- XOL-490/493 (workstream C): the site link is now a standalone
             sibling block, last inside the popover, rather than the advisory
             block's footer — so it renders for ANY row with a site, not only
             one whose only remaining issue is an advisory-block slug. The
             adjacent-sibling selector below draws a lighter footer rule ONLY
             when something precedes this block; a link-only popover (no
             matching block above, e.g. an unrecognised slug) therefore gets a
             clean unruled line for free — no v-if, no computed, no branch.
             XOL-493 C5: a row with no site.id AND no matching block above
             still renders nothing here — there is nothing to offer, and that
             is intended, not an oversight. -->
        <div v-if="row.site && row.site.id" class="audit-reconcile-cell__site-link">
          <router-link
            :to="'/sites/d.site.' + row.site.id + '.finance.sharers'"
            target="_blank"
            aria-label="Open this site's Sharers tab in a new tab"
            class="audit-reconcile-cell__open-site">
            Open this site's Sharers tab <span aria-hidden="true">↗</span>
          </router-link>
        </div>

      </div>

      <!-- Trigger button -->
      <el-button
        slot="reference"
        size="mini"
        type="primary"
        icon="fa fa-wrench">
        Reconcile
      </el-button>

    </el-popover>

  </div>
</template>

<script>

import axios from '@/lib/axiosClient'
import PostMixins from '@/mixins/PostMixins'
import NumberMixins from '@/mixins/NumberMixins'
import DateMixins from '@/mixins/DateMixins'

import SelectSharerRates from '@/components/atoms/selects/SelectSharerRates'
import DateInput from '@/components/atoms/inputs/DateInput'
import CurrencyInput from '@/components/atoms/inputs/CurrencyInput'

import { isAbsent, isPresent, dateChainErrors } from '@/lib/licenseeDates'

// Phase 2 + XOL-376 Inc 4 + XOL-416: issue types that require manual review —
// no one-click reconcile action.
//
// This list encodes ACTIONABILITY, not severity. `duplicate_sharer_no` and
// `zero_sharer_no` are severity `error` (silent non-billing and a shared telco
// column are money defects) yet still belong here: only a super can decide which
// sharer keeps a contested slot, or what number an unassigned one should take.
// An error with no safe auto-resolve is exactly what "Needs manual review" is for.
//
// XOL-487: the two XOL-462 strict-rate slugs joined the list (commit
// 3192a119). They were added to LicenseeAudit's label map and chip order when
// XOL-462 landed, but not here — and because every other block in the popover
// is gated on a specific slug, a row whose ONLY issue was a strict violation
// opened a popover with no blocks at all: no action, no explanation, not even
// the "Open site" link. They belong here for the same reason as the pair
// above: the fix is a different rate or a corrected sharer number, and
// neither is a safe one-click resolve from the audit table.
//
// position_below_min_sharer (XOL-486/487) is now added for the same reason —
// this was the reserved slot commit 3192a119 left for it.
//
// XOL-490: the API's now-retired first-sharer-only-violation detector (and
// its slug) is removed from this list. Its columns were dropped in API commit
// 67bff36b, and that removal is now deployed — the slug can no longer occur.
// Its removal here was previously blocked on "an empty popover with the label
// rendering as a raw slug" (the XOL-487 lockstep note this replaces); XOL-493
// workstream C (the site-link move below) closes that hazard for every row,
// not just this one, so this and A land together.
var ADVISORY_TYPES = ['orphan_version_pin', 'no_structural_match', 'missing_structure_height', 'ambiguous_billing_mode', 'duplicate_sharer_no', 'zero_sharer_no', 'position_below_min_sharer', 'strict_position_out_of_range', 'strict_term_exceeded']

export default {
  name: 'audit-reconcile-cell',

  mixins: [PostMixins, NumberMixins, DateMixins],

  components: {
    SelectSharerRates,
    DateInput,
    CurrencyInput
  },

  props: {
    row: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    // Phase 0: /licensees/no-rate rows have no issues[] — fall back to
    // the implicit no_rate issue so the assign-rate block always renders.
    // Phase 1 /licensees/audit rows will carry real issues[], which take over.
    effectiveIssues: function () {
      return (this.row.issues && this.row.issues.length)
        ? this.row.issues
        : [{ type: 'no_rate' }]
    },

    issueTypes: function () {
      return this.effectiveIssues.map(function (i) { return i.type })
    },

    // Phase 2: filter effectiveIssues to advisory-only types for the advisory block.
    advisoryIssues: function () {
      return this.effectiveIssues.filter(function (i) {
        return ADVISORY_TYPES.indexOf(i.type) >= 0
      })
    },

    // XOL-467 rectify redesign: the unified "Set dates" block gates on
    // either date issue — the branching that used to route between a
    // Handover-only block and a separate "Set Agreement Start" block
    // (XOL-483/484) is gone; all three fields render every time either
    // issue is present.
    // XOL-471: `billing_before_started` joins the two missing-date slugs — moving
    // Billing Start forward (or Agreement Start back) fixes it, and this form does
    // both. `expiry_before_billing_start` deliberately does NOT route here: where
    // `started` already equals `startBilling` (common-tower id 385), no billing
    // start satisfies both `started <= startBilling` and `< expiry`, so only the
    // EXPIRY can move. That slug goes to Extend agreement instead.
    //
    // These two slugs share their names with client-side codes in `dateChainErrors`
    // (src/lib/licenseeDates.js) by design — one vocabulary per concept — but a
    // per-field validation code and a row-level audit issue type stay different
    // things. Do not assume a match in one implies the other.
    showDateBlock: function () {
      return this.issueTypes.indexOf('no_handover_or_agreement_start') >= 0 ||
        this.issueTypes.indexOf('no_billing_start') >= 0 ||
        this.issueTypes.indexOf('billing_before_started') >= 0
    },

    // XOL-483 (reviewer finding 3), carried into the XOL-467 redesign: the
    // API computes distinct `detail` strings per row (LicenseeBillingAudit —
    // both missing / commencement-only missing / handover-only missing /
    // no_billing_start), so a static local sentence can't tell those apart.
    // Prefer `no_handover_or_agreement_start`'s detail (the more foundational
    // issue) over `no_billing_start`'s when a row somehow carries both —
    // order is deterministic regardless of how the API ordered `issues[]`.
    // Prefers the API's per-row `detail` over any local sentence — it names the
    // actual dates and why they are wrong, which a generic string cannot.
    //
    // XOL-471: `billing_before_started` MUST be in this list. Without it a row
    // flagged only for ordering fell through to the fallback, which says dates
    // are MISSING — false for that row: nothing is absent, the order is wrong.
    // Caught in a browser, not by a test.
    dateIssueDetail: function () {
      var issues = this.effectiveIssues
      var slugs = [
        'no_handover_or_agreement_start',
        'no_billing_start',
        'billing_before_started'
      ]

      for (var n = 0; n < slugs.length; n++) {
        var slug = slugs[n]
        var hit = issues.filter(function (i) {
          return i.type === slug && i.detail
        })[0]
        if (hit) return hit.detail
      }

      // Deliberately does not assert WHAT is wrong — this runs only when the API
      // sent no detail, and a wrong guess reads worse than a vague truth.
      return 'Check this sharer\'s agreement dates.'
    },

    // Orchestrator addendum (2026-09-02) — pinned API-side by
    // `test_nulling_both_dates_on_a_handover_row_rewrites_them_from_the_handover`:
    // `LicenseeRequest::resolvedDate()` is `exists($key) ? input($key) :
    // stored`, so an explicit null WINS over a stored value. On a row with a
    // Handover, posting null for BOTH Agreement Start and Billing Start hits
    // the cascade's both-null branch, which re-derives both from the
    // Handover rather than clearing them. Clearing Billing Start ALONE
    // (Agreement Start still holds a value) does NOT trip this — that is the
    // main, intentionally-unwarned case (Carlos, 2026-09-02: "it is okay for
    // a site to be left with billing start unset").
    willRederiveFromHandover: function () {
      return isPresent(this.dateHandover) && isAbsent(this.dateStarted) && isAbsent(this.dateStartBilling)
    },

    // XOL-481: what taking the sharer off the lock would leave behind — a
    // real rate, or nothing. `calculatedRate` is the API's AUTO value
    // (SharerController::calculatedRateFor), null when the licensee has no
    // rate_id / rate_version_id, or when its billing position is 0. This is
    // context for the customer's choice, NOT a signal that one exit is wrong:
    // clearing the override is a legitimate answer either way, and a row that
    // lands in `no_rate` is one that gets resolved there. Treat `undefined`
    // exactly like `null` — Phase-0 `/licensees/no-rate` rows built by
    // `noRateAuditRow()` carry no `calculatedRate` key at all.
    hasCalculatedFallback: function () {
      var v = this.row.calculatedRate
      return v !== null && v !== undefined && Number(v) > 0
    },

    // Formatted read-out of the same value; em dash when there is no rate
    // behind the override. Single source for that decision — the template
    // renders this directly rather than re-deciding with its own ternary,
    // which would disagree for `calculatedRate: 0` (hasCalculatedFallback is
    // false there, but formatMoney(0) is a perfectly renderable "RM0.00").
    // Mirrors FinanceSharer.calcRateDisplay (FinanceSharer.vue:359).
    calcRateDisplay: function () {
      if (!this.hasCalculatedFallback) {
        return '—'
      }
      return this.formatMoney(this.row.calculatedRate, 2)
    }
  },

  methods: {
    // Accept the backend-suggested rate directly (seeds rateId + triggers assign).
    acceptSuggested: function () {
      if (this.row.suggestedRate) {
        this.rateId = this.row.suggestedRate.id
        this.assign()
      }
    },

    // Resolve the display name for the selected rate.
    // Prefer the suggestedRate name when the ids match; otherwise look up
    // from the SelectSharerRates options list via the component ref.
    resolveRateName: function () {
      var id = this.rateId
      if (this.row.suggestedRate && this.row.suggestedRate.id === id) {
        return this.row.suggestedRate.name
      }
      var selectRef = this.$refs.rateSelect
      if (selectRef && selectRef.options) {
        var match = selectRef.options.find(function (o) { return o.id === id })
        if (match) return match.name
      }
      return 'the selected rate'
    },

    // Shared confirm + saveForm helper. Reduces per-action boilerplate.
    // opts: { title, message, confirmText, formData, onError }
    // successCb is called on success (before emit + close). `onError`
    // (XOL-467) is the failCallback — was hardcoded `null`, so a 422 closed
    // nothing but also SHOWED nothing beyond the generic per-field toast
    // PostMixins.send already raises; callers that want an inline message
    // (the Set handover block) pass one.
    doReconcile: function (opts) {
      var self = this
      this.$confirm(opts.message, opts.title, {
        confirmButtonText: opts.confirmText || 'Confirm',
        cancelButtonText: 'Cancel',
        type: opts.type || 'warning',
        center: true
      }).then(function () {
        self.saveForm(
          self,
          '/licensees',
          self.row.licensee_id,
          opts.formData,
          'licensee',
          function (data) {
            self.$emit('resolved')
            if (self.$refs.popover) {
              self.$refs.popover.doClose()
            }
          },
          opts.onError || null,
          true,
          'audittable'
        )
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    assign: function () {
      if (!this.rateId) return

      var self = this
      var rateName = this.resolveRateName()
      var orgName = this.row.organisation && this.row.organisation.name
      var siteCode = this.row.site && this.row.site.siteId
      var legacyBill = this.formatMoney(this.row.currentLegacyBill || 0, 2)

      var message = 'Assign "' + rateName + '" to ' + orgName + ' at ' + siteCode + '?' +
        ' This sharer is currently billed ' + legacyBill + ' (legacy).<br/>' +
        'Fees will recalculate in the background — this may take a moment.'

      this.$confirm(message, 'Assign rate', {
        confirmButtonText: 'Assign rate',
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        center: true,
        type: 'warning'
      }).then(function () {
        var formData = {
          rate_id: self.rateId,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }

        self.saveForm(
          self,
          '/licensees',
          self.row.licensee_id,
          formData,
          'licensee',
          function (data) {
            // Success: emit resolved, close the popover.
            // The audittable tableRef makes saveForm fire the refresh-table event
            // so the resolved row drops on re-fetch — do NOT splice rows manually.
            self.$emit('resolved')
            if (self.$refs.popover) {
              self.$refs.popover.doClose()
            }
          },
          null,   // failCallback — saveForm already toasts on error; keep popover open
          true,   // showMessage
          'audittable'
        )
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    // XOL-467 rectify redesign: field -> message for whatever
    // `dateChainErrors()` reports, mirroring ProjectTab.vue's /
    // FinanceSharer.vue's own `dateChainCopy()`. `expiry` has no input in
    // this popover (it lives in the "Extend agreement" block above) — both
    // expiry-triggered rules reroute onto Billing Start, the only field here
    // either rule can point at.
    dateChainCopy: function (item) {
      switch (item.code) {
        case 'handover_required':
          return { field: 'handover', message: 'Required when Agreement Start is set.' }
        case 'started_required':
          return { field: 'started', message: 'Required when Billing Start is set.' }
        case 'billing_start_required':
          return { field: 'startBilling', message: 'Required — this agreement has an expiry date set.' }
        case 'billing_before_started':
          return {
            field: 'startBilling',
            message: 'Cannot be before Agreement Start (' + this.dateStarted + ').'
          }
        case 'expiry_before_billing_start':
          return {
            field: 'startBilling',
            message: 'Conflicts with this agreement\'s expiry (' + this.row.expiry + ') — must be earlier.'
          }
        default:
          return null
      }
    },

    // XOL-467 rectify redesign: synchronous, hand-rolled — same reasoning as
    // ProjectTab/FinanceSharer (`this.row` here is a plain prop, not an
    // el-form `:model`, so Element's own `validate()` is unusable). Reads
    // the row's STORED `expiry` — this form has no expiry input, but rule 1
    // (`expiry` requires `startBilling`) can still fire if a user clears
    // Billing Start on a row that already has an expiry. Update mode: no
    // `isCreate` — this always PUTs an existing licensee.
    validateDates: function () {
      var self = this
      var chainRow = {
        handover: this.dateHandover,
        started: this.dateStarted,
        startBilling: this.dateStartBilling,
        expiry: this.row.expiry
      }
      var errors = dateChainErrors(chainRow)
      var map = {}

      errors.forEach(function (item) {
        var copy = self.dateChainCopy(item)
        if (copy && !map[copy.field]) {
          map[copy.field] = copy.message
        }
      })

      this.dateFieldErrors = map
      return Object.keys(map).length === 0
    },

    // Errors clear on ANY of the three fields changing — the whole map,
    // since the rules are cross-field (filling Handover can resolve a
    // message shown on Agreement Start).
    clearDateFieldErrors: function () {
      this.dateFieldErrors = {}
    },

    // Plain-English description of what is about to be written, including
    // dates being left unset — reused by the confirm message so the user
    // sees the same sentence structure the block does.
    describeDates: function (handoverStr, startedStr, startBillingStr) {
      var parts = [
        handoverStr ? ('Handover to ' + handoverStr) : 'leave Handover unset',
        startedStr ? ('Agreement Start to ' + startedStr) : 'leave Agreement Start unset',
        startBillingStr ? ('Billing Start to ' + startBillingStr) : 'leave Billing Start unset'
      ]
      return parts.join(', ')
    },

    // XOL-467 rectify redesign: ONE form, ONE submit — replaces `setHandover`
    // / `setAgreementStart` / `setBillingStart`. Always posts all three date
    // keys, explicit `null` for whichever is left/cleared (the API's rules
    // are `sometimes|nullable|date` — `nullable` is load-bearing: an explicit
    // null clears the stored value rather than being skipped). Billing Start
    // is never required here — see the template comment on that field.
    saveDates: function () {
      this.dateFieldErrors = {}
      if (!this.validateDates()) return

      var self = this
      var handoverStr = this.formatDateSql(this.dateHandover)
      var startedStr = this.formatDateSql(this.dateStarted)
      var startBillingStr = this.formatDateSql(this.dateStartBilling)

      this.doReconcile({
        title: 'Set dates',
        message: 'Set ' + this.describeDates(handoverStr, startedStr, startBillingStr) +
          '? Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Set dates',
        type: 'warning',
        formData: {
          handover: handoverStr,
          started: startedStr,
          startBilling: startBillingStr,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        },
        onError: function (errors) {
          var map = {}
          if (errors && errors.handover) map.handover = errors.handover[0]
          if (errors && errors.started) map.started = errors.started[0]
          if (errors && errors.startBilling) map.startBilling = errors.startBilling[0]
          if (Object.keys(map).length === 0) {
            map.startBilling = 'Could not save these dates.'
          }
          self.dateFieldErrors = map
        }
      })
    },

    // Phase 1: extend agreement expiry (expired_agreement / expiring_soon).
    // XOL-483 (reviewer finding 4): same shape as setBillingStart — one date
    // key arms the row check, and the missing link resolves from the stored
    // row. Measured: 48 of 231 rows in this bucket have no stored handover,
    // so extending 422s on `handover_required` for those. `onError` surfaces
    // that inline instead of leaving it to the generic toast.
    extendExpiry: function () {
      if (!this.expiryDate) return
      var self = this
      var dateStr = this.formatDateSql(this.expiryDate)
      this.expiryError = null
      this.doReconcile({
        title: 'Extend agreement',
        message: 'Extend the agreement expiry to ' + dateStr + '? Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Extend',
        type: 'warning',
        formData: {
          expiry: dateStr,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        },
        onError: function (errors) {
          var message = (errors && errors.handover && errors.handover[0]) ||
            (errors && errors.started && errors.started[0]) ||
            (errors && errors.startBilling && errors.startBilling[0]) ||
            'Could not extend the agreement — fix its dates on the Project tab first.'
          self.expiryError = message
        }
      })
    },

    // Phase 1: soft-end terminate (expired_agreement / expiring_soon).
    // Billing stops forward, row preserved — NOT a destroy().
    softTerminate: function () {
      var self = this
      var today = this.formatDateSql(new Date())
      this.doReconcile({
        title: 'Terminate licensee',
        message: 'Soft-terminate this licensee as of today (' + today + ')? Billing will stop going forward. Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Terminate',
        type: 'warning',
        formData: {
          status: 'Terminated',
          terminated: today,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }
      })
    },

    // XOL-481: clear the fee-input error the moment the user commits a new
    // amount via @change (blur).
    clearOverrideError: function () {
      this.overrideError = null
    },

    // XOL-481: set the fee (PUT /licensees/{id}/rate-override, { currentRate }).
    // Modelled line-for-line on clearOverride() below — same $confirm ->
    // send() shape, same positional arguments, same success handling.
    //
    // Guard is stricter than the API on purpose: the API accepts
    // `currentRate: 0` ('required|numeric|min:0'), but 0 IS the
    // empty_override state being fixed — saving it would silently recreate
    // the exact issue. Two messages, not one and not four: "you entered
    // nothing" (null/undefined/''/NaN — non-numeric folds in here, as it is
    // effectively unreachable through a type="number" input) versus "you
    // entered zero" (<=0 — negatives fold in with zero, same rule).
    saveOverride: function () {
      var raw = this.overrideAmount
      var amount = Number(raw)
      if (raw === null || raw === undefined || raw === '' || isNaN(amount)) {
        this.overrideError = 'Enter the fee before saving.'
        return
      }
      if (amount <= 0) {
        this.overrideError = 'The fee must be more than zero — a zero fee is the state you\'re fixing.'
        return
      }

      var self = this
      var orgName = this.row.organisation && this.row.organisation.name
      var siteId = this.row.site && this.row.site.siteId

      // No dangerouslyUseHTMLString — the message interpolates an org name
      // (user-controlled data), same as clearOverride below.
      this.$confirm(
        'Set the fee for ' + orgName + ' at ' + siteId + ' to ' + this.formatMoney(amount, 2) +
          '? This locks the fee at that amount until the override is cleared. ' +
          'Fees will recalculate in the background — this may take a moment.',
        'Set fee',
        {
          confirmButtonText: 'Set fee',
          cancelButtonText: 'Cancel',
          type: 'warning',
          center: true
        }
      ).then(function () {
        self.send(
          self,
          self.getEndpoint('/licensees/' + self.row.licensee_id + '/rate-override'),
          // Two values, two jobs (XOL-380): the request body carries the raw
          // Number, never the formatMoney display string used above.
          { currentRate: amount },
          'Rate override',
          function (data) {
            // Clear the field on success. Usually invisible — the row leaves
            // the bucket on the table refresh — but a co-flagged row still
            // renders under `all` and under its other chips, so the same cell
            // instance can be reopened. Without this it would show a stale
            // amount in a field whose issue is already resolved.
            self.overrideAmount = null
            self.overrideError = null
            self.$emit('resolved')
            if (self.$refs.popover) self.$refs.popover.doClose()
          },
          function (errors) {
            // failCallback receives EITHER a validation map (data.errors
            // branch, PostMixins.js:305-307) OR an Error (403/418/data.error
            // branches, which fire only because notifyFailureCallback:true).
            // Handle both, exactly as extendExpiry does. The fallback names
            // a recovery surface rather than blaming the input, so it stays
            // true for a 403 as well as a network failure.
            self.overrideError = (errors && errors.currentRate && errors.currentRate[0]) ||
              'Could not set the fee. Try again, or set it on the site\'s Finance tab.'
          },
          axios.put,
          true,
          'Save',
          'audittable',
          true
        )
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    // Phase 2: clear rate override (empty_override; `override_without_rate`
    // was retired in XOL-471).
    // DELETE /licensees/{id}/rate-override — mirrors FinanceSharer.clearRateOverride.
    //
    // XOL-481: the confirm message is conditional on hasCalculatedFallback.
    // Clearing is one of the issue's two legitimate exits, not a mistake —
    // the message states the real outcome without discouraging the action.
    clearOverride: function () {
      var self = this
      var orgName = this.row.organisation && this.row.organisation.name
      var siteId = this.row.site && this.row.site.siteId

      var message = this.hasCalculatedFallback
        ? 'Clear the rate override for ' + orgName + ' at ' + siteId +
          '? Billing resumes from the calculated rate. Fees will recalculate in the background — this may take a moment.'
        : 'Clear the rate override for ' + orgName + ' at ' + siteId +
          '? There is no rate behind it, so this sharer will have no fee until a rate is assigned — ' +
          'it will appear under No rate in this audit. Fees will recalculate in the background — this may take a moment.'

      this.$confirm(
        message,
        'Clear rate override',
        {
          confirmButtonText: 'Clear override',
          cancelButtonText: 'Cancel',
          type: 'warning',
          center: true
        }
      ).then(function () {
        self.send(
          self,
          self.getEndpoint('/licensees/' + self.row.licensee_id + '/rate-override'),
          null,
          'Rate override',
          function (data) {
            self.$emit('resolved')
            if (self.$refs.popover) self.$refs.popover.doClose()
          },
          null,
          axios.delete,
          true,
          'Save',
          'audittable',
          true
        )
      }).catch(function () {
        // Confirm cancelled — no-op
      })
    },

    // Phase 2: set termination end date (terminated_no_end).
    // The licensee is already Terminated — this fills in the missing end date.
    setEndDate: function () {
      if (!this.endedDate) return
      var self = this
      var dateStr = this.formatDateSql(this.endedDate)
      this.doReconcile({
        title: 'Set end date',
        message: 'Set the termination end date to ' + dateStr + '? Fees will recalculate in the background — this may take a moment.',
        confirmText: 'Set end date',
        type: 'warning',
        formData: {
          terminated: dateStr,
          site_id: self.row.site && self.row.site.id,
          organisation_id: self.row.organisation && self.row.organisation.id
        }
      })
    }
  },

  data: function () {
    return {
      // no_rate
      rateId: (this.row && this.row.suggestedRate) ? this.row.suggestedRate.id : null,
      // XOL-467 rectify redesign: no_handover_or_agreement_start /
      // no_billing_start — one unified "Set dates" block, all three fields
      // pre-filled from the row's own stored value where it has one, left
      // empty where it doesn't. `isAbsent` treats '0000-00-00' as absent,
      // same as the API's own DateAnchor::normalize() — no zero-date
      // normalisation is guaranteed from the API.
      dateHandover: (this.row && isPresent(this.row.handover)) ? this.row.handover : null,
      dateStarted: (this.row && isPresent(this.row.started)) ? this.row.started : null,
      dateStartBilling: (this.row && isPresent(this.row.startBilling)) ? this.row.startBilling : null,
      dateFieldErrors: {},
      // expired_agreement / expiring_soon
      expiryDate: null,
      expiryError: null,
      // terminated_no_end
      endedDate: null,
      // XOL-481 empty_override: the negotiated flat fee the user is setting.
      // Deliberately NOT seeded from row.currentRate — on every row this block
      // renders on, currentRate is 0 (that IS the issue), and pre-filling 0 into a
      // field whose guard rejects 0 is hostile.
      overrideAmount: null,
      overrideError: null
    }
  },

  watch: {
    // Re-seed rateId if the row prop changes (e.g. after a table refresh).
    'row.suggestedRate': function (val) {
      if (val && !this.rateId) {
        this.rateId = val.id
      }
    }
  }
}
</script>

<style scoped>
.audit-reconcile-cell__readonly {
  color: #909399;
  font-size: 16px;
}

.audit-reconcile-cell__popover {
  padding: 4px 0;
}

.audit-reconcile-cell__block + .audit-reconcile-cell__block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
}

.audit-reconcile-cell__label {
  font-weight: 600;
  margin: 0 0 8px;
  font-size: 13px;
}

.audit-reconcile-cell__suggested {
  margin-bottom: 8px;
}

.audit-reconcile-cell__footer {
  margin-top: 10px;
  text-align: right;
}

.audit-reconcile-cell__footer--spread {
  display: flex;
  justify-content: space-between;
}

.audit-reconcile-cell__hint {
  color: #909399;
  font-size: 12px;
  margin: 0 0 6px;
}

/* XOL-467 rectify redesign: spacing between the three stacked date-inputs in
   the "Set dates" block — DateInput's `:no-form` wrapper is a plain div with
   no margin of its own. */
.audit-reconcile-cell__field + .audit-reconcile-cell__field {
  margin-top: 8px;
}

.audit-reconcile-cell__field-label {
  color: #606266;
  font-size: 12px;
  margin: 0 0 4px;
}

/* XOL-467: this cell renders inside an el-table cell, and Element UI sets
   `word-break: break-all` there, which cascades in and wraps per CHARACTER
   ("has nothin / g behind it", "S / et Handover"). It was invisible while the
   hint was a short static string; deferring to the API's per-row `issue.detail`
   made the text long enough to wrap, and the breakage showed up. Same trap and
   same fix as SitesTable.vue:708. Applies to the failure text and the
   re-derive warning too — both can run just as long as the API's own copy. */
::v-deep .el-table .cell .audit-reconcile-cell__hint,
::v-deep .el-table .cell .audit-reconcile-cell__error,
::v-deep .el-table .cell .audit-reconcile-cell__warning,
::v-deep .el-table .cell .audit-reconcile-cell__readout,
.audit-reconcile-cell__hint,
.audit-reconcile-cell__error,
.audit-reconcile-cell__warning,
.audit-reconcile-cell__readout {
  word-break: normal;
  overflow-wrap: break-word;
}

/* XOL-481: the calculated-rate / legacy-bill readout rows. Flex space-between
   with white-space:nowrap on the value so "RM1,500.00" never wraps or breaks
   mid-number regardless of label length. */
.audit-reconcile-cell__readout {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 12px;
}

.audit-reconcile-cell__readout-label {
  color: #909399;
  margin: 0;
}

.audit-reconcile-cell__readout-value {
  color: #303133;
  font-weight: 600;
  white-space: nowrap;
}

/* XOL-467: these date inputs are `:no-form`, so DateInput's `error` prop
   (an el-form-item prop) is inert — render a save failure as plain text. */
.audit-reconcile-cell__error {
  color: #F56C6C;
  font-size: 12px;
  margin: 8px 0 0;
  line-height: 1.5;
}

/* XOL-467 (2026-09-02 addendum): non-blocking notice that clearing BOTH
   Agreement Start and Billing Start on a row with a Handover re-derives them
   from it rather than clearing them — Element's own warning colour, not the
   error red, because the save is not blocked. */
.audit-reconcile-cell__warning {
  color: #E6A23C;
  font-size: 12px;
  margin: 8px 0 0;
  line-height: 1.5;
}

.audit-reconcile-cell__block--advisory {
  background: #f5f7fa;
  border-radius: 4px;
  padding: 8px 10px;
}

.audit-reconcile-cell__label--muted {
  color: #909399;
  font-weight: 600;
}

.audit-reconcile-cell__advisory-list {
  margin: 4px 0 0;
  padding-left: 16px;
  font-size: 12px;
  color: #606266;
}

.audit-reconcile-cell__advisory-item {
  margin-bottom: 4px;
}

.audit-reconcile-cell__open-site {
  font-size: 12px;
  color: #409eff;
  text-decoration: none;
}

.audit-reconcile-cell__open-site:hover {
  text-decoration: underline;
}

/* XOL-493 workstream C: the site link is a sibling block now, not the
   advisory block's footer. It needs its own, lighter separator — the
   __block + __block rule above (16px margin/padding + a rule) would give one
   navigation link the same visual weight as a reconcile action. This rule
   only fires when something precedes the link (the adjacent-sibling
   selector), so a link-only popover (no matching block above) renders a
   clean, unruled line for free. */
.audit-reconcile-cell__site-link {
  font-size: 12px;
}

.audit-reconcile-cell__block + .audit-reconcile-cell__site-link {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid #ebeef5;
}
</style>
